import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Ban,
  CheckCircle2,
  FileText,
  Loader2,
  Pencil,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { formatPeriodLabel } from "@/components/payroll/format";
import { LIQUIDATION_STATUS_BADGE_VARIANT } from "@/components/payroll/liquidation-format";
import { LiquidationForm } from "@/components/payroll/LiquidationForm";
import { DiscardDraftDialog } from "@/components/shared/DiscardDraftDialog";
import {
  useConfirmLiquidation,
  useDiscardLiquidation,
  useLiquidation,
  useUpdateLiquidation,
  useVoidLiquidation,
} from "@/lib/api/payroll-liquidations.queries";
import { usePayrollEmployee } from "@/lib/api/payroll-employees.queries";
import { translateApiError } from "@/lib/translate-api-error";
import { translateDraftError } from "@/lib/draft-errors";
import { useTeamPermission } from "@/lib/team-context";
import { ApiError } from "@/lib/api";
import { useActiveCompany } from "@/lib/company-context";
import { DocumentJournalPanel } from "@/components/journal/DocumentJournalPanel";
import type { LiquidacionResult } from "@/lib/api/payroll-liquidations";

export const Route = createFileRoute(
  "/companies/$companyId/nomina/liquidaciones/$liquidationId",
)({
  component: LiquidationDetailPage,
});

function LiquidationDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <LiquidationDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function LiquidationDetailContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId, liquidationId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const detailQuery = useLiquidation(companyId, liquidationId);
  const confirmMutation = useConfirmLiquidation(companyId);
  const voidMutation = useVoidLiquidation(companyId);
  const discardMutation = useDiscardLiquidation(companyId);
  const canWrite = useTeamPermission("payroll.write");
  const [editing, setEditing] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const employeeQuery = usePayrollEmployee(
    companyId,
    detailQuery.data?.employeeId,
  );
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [resolutionMissing, setResolutionMissing] = useState(false);
  const [contextChanged, setContextChanged] = useState(false);
  const updateMutation = useUpdateLiquidation(companyId);

  const result = useMemo(
    () => (detailQuery.data?.result ?? {}) as Partial<LiquidacionResult>,
    [detailQuery.data],
  );

  if (detailQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <ErrorState
        title={t("liquidations.detail.error_title")}
        description={t("liquidations.detail.error_body")}
        error={detailQuery.error ?? undefined}
        onRetry={() => void detailQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const liquidation = detailQuery.data;
  // Only a draft can be reopened; once confirmed the figures (and their postings) are final (PF-463).
  const canEdit = canWrite && liquidation.editable;
  const canDiscard = canWrite && liquidation.status === "draft";
  const employee = employeeQuery.data;
  const devengados = result.devengados;
  const deducciones = result.deducciones;
  const aportes = result.aportesPatronales;
  const provisiones = result.provisiones;
  const ibcDetalle = result.ibcDetalle;

  async function handleConfirm() {
    setResolutionMissing(false);
    try {
      // Confirm exactly the revision on screen: if someone saved since, the server refuses (409 DRAFT_STALE).
      await confirmMutation.mutateAsync({
        liquidationId,
        expectedRevision: liquidation.revision,
      });
      toast.success(t("liquidations.toast.confirmed"));
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.code === "NOMINA_RESOLUTION_REQUIRED"
      ) {
        setResolutionMissing(true);
        toast.error(t("liquidations.error.NOMINA_RESOLUTION_REQUIRED"));
      } else if (
        err instanceof ApiError &&
        err.code === "LIQUIDATION_MONTH_CONTEXT_CHANGED"
      ) {
        // Computed against a month that has since changed: recalculate, review, confirm again.
        setContextChanged(true);
        toast.error(t("liquidations.error.LIQUIDATION_MONTH_CONTEXT_CHANGED"));
      } else {
        toast.error(
          translateDraftError(err, t) ??
            translateApiError(err, t, "liquidations.error"),
        );
      }
    }
  }

  async function handleRecalculate() {
    try {
      await updateMutation.mutateAsync({
        liquidationId,
        input: { expectedRevision: liquidation.revision },
      });
      setContextChanged(false);
      toast.success(t("liquidations.toast.recalculated"));
    } catch (err) {
      toast.error(
        translateDraftError(err, t) ??
          translateApiError(err, t, "liquidations.error"),
      );
    }
  }

  async function handleDiscard() {
    try {
      await discardMutation.mutateAsync({
        liquidationId,
        expectedRevision: liquidation.revision,
      });
      toast.success(t("liquidations.toast.discarded"));
      setDiscardOpen(false);
      void navigate({
        to: "/companies/$companyId/nomina/liquidaciones",
        params: { companyId },
      });
    } catch (err) {
      toast.error(
        translateDraftError(err, t) ??
          translateApiError(err, t, "liquidations.error"),
      );
      setDiscardOpen(false);
    }
  }

  async function handleVoid() {
    if (!voidReason.trim()) {
      toast.error(t("treasury.void.reason_required"));
      return;
    }
    try {
      await voidMutation.mutateAsync({
        liquidationId,
        reason: voidReason.trim(),
      });
      toast.success(t("liquidations.toast.voided"));
      setVoidOpen(false);
      setVoidReason("");
    } catch (err) {
      toast.error(translateApiError(err, t, "liquidations.error"));
    }
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/nomina/liquidaciones"
            params={{ companyId }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("liquidations.detail.back")}
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              {employee
                ? `${employee.firstName} ${employee.lastName}`
                : t("liquidations.detail.title")}
            </h1>
            <Badge
              variant={LIQUIDATION_STATUS_BADGE_VARIANT[liquidation.status]}
            >
              {t(`liquidations.status.${liquidation.status}`)}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {formatPeriodLabel(liquidation.periodStart, liquidation.periodEnd)}
            {result.workedDays !== undefined
              ? ` · ${t("liquidations.detail.worked_days", { days: result.workedDays })}`
              : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && !editing ? (
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              {t("liquidations.actions.edit")}
            </Button>
          ) : null}
          {canDiscard && !editing ? (
            <Button
              variant="outline"
              onClick={() => setDiscardOpen(true)}
              disabled={discardMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
              {t("liquidations.actions.discard")}
            </Button>
          ) : null}
          {liquidation.status === "draft" && !editing ? (
            <Button
              onClick={() => void handleConfirm()}
              disabled={confirmMutation.isPending}
            >
              <CheckCircle2 className="h-4 w-4" />
              {t("liquidations.actions.confirm")}
            </Button>
          ) : null}
          {liquidation.status !== "void" && !editing ? (
            <Button
              variant="destructive"
              onClick={() => setVoidOpen(true)}
              disabled={voidMutation.isPending}
            >
              <Ban className="h-4 w-4" />
              {t("liquidations.actions.void")}
            </Button>
          ) : null}
        </div>
      </div>

      {editing ? (
        <LiquidationForm
          companyId={companyId}
          initialLiquidation={liquidation}
          onSaved={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          {resolutionMissing ? (
            <Card className="border-amber-300 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-900/20">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6 text-sm text-amber-900 dark:text-amber-100">
                <p>{t("liquidations.error.NOMINA_RESOLUTION_REQUIRED")}</p>
                <Button asChild variant="outline" size="sm">
                  <Link
                    to="/companies/$companyId/resoluciones"
                    params={{ companyId }}
                  >
                    {t("liquidations.detail.go_resolutions")}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : null}

          {contextChanged && liquidation.status === "draft" ? (
            <Card
              className="border-amber-300 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-900/20"
              data-testid="liq-context-changed"
            >
              <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6 text-sm text-amber-900 dark:text-amber-100">
                <p>{t("liquidations.detail.context_changed")}</p>
                {canEdit ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleRecalculate()}
                    disabled={updateMutation.isPending}
                    data-testid="liq-recalculate"
                  >
                    {updateMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    {t("liquidations.actions.recalculate")}
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          {/* Headline numbers */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="border-emerald-200 dark:border-emerald-900/40">
              <CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("liquidations.detail.net_payable")}
                </p>
                <p className="mt-1 font-mono text-2xl font-semibold text-emerald-700 dark:text-emerald-300">
                  {formatCop(liquidation.netPayable)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  IBC
                </p>
                <p className="mt-1 font-mono text-2xl font-semibold">
                  {formatCop(liquidation.ibcAmount)}
                </p>
                {ibcDetalle ? (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {t("liquidations.detail.ibc_detail", {
                      smmlv: ibcDetalle.smmlvMultiplo.toFixed(2),
                    })}
                    {ibcDetalle.contributionDays != null
                      ? ` · ${t("liquidations.detail.contribution_days", { days: ibcDetalle.contributionDays })}`
                      : ""}
                    {ibcDetalle.pisoAplicado
                      ? ` · ${t("liquidations.detail.ibc_floor")}`
                      : ""}
                    {ibcDetalle.techoAplicado
                      ? ` · ${t("liquidations.detail.ibc_ceiling")}`
                      : ""}
                  </p>
                ) : null}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("liquidations.detail.employer_cost")}
                </p>
                <p className="mt-1 font-mono text-2xl font-semibold">
                  {formatCop(liquidation.totalEmployerCost)}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Devengados */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("liquidations.detail.devengados")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                {devengados ? (
                  <>
                    <Row
                      label={t("liquidations.detail.basic")}
                      value={formatCop(devengados.basic)}
                    />
                    {devengados.transportAllowance > 0 ? (
                      <Row
                        label={t("liquidations.detail.transport")}
                        value={formatCop(devengados.transportAllowance)}
                      />
                    ) : null}
                    {(devengados.horasExtra ?? []).map((he, idx) => (
                      <Row
                        key={`${he.type}-${idx}`}
                        label={`${t(`liquidations.he.${he.type}`)} (${he.hours}h × ${he.factor})`}
                        value={formatCop(he.amount)}
                      />
                    ))}
                    {devengados.comisiones > 0 ? (
                      <Row
                        label={t("liquidations.form.comisiones")}
                        value={formatCop(devengados.comisiones)}
                      />
                    ) : null}
                    {devengados.bonificacionesSalariales > 0 ? (
                      <Row
                        label={t("liquidations.form.bonif_salarial")}
                        value={formatCop(devengados.bonificacionesSalariales)}
                      />
                    ) : null}
                    {(devengados.salariedTravelAllowance ?? 0) > 0 && (
                      <Row
                        label={t("payrollTravel.salaried")}
                        value={formatCop(devengados.salariedTravelAllowance)}
                      />
                    )}
                    {(devengados.nonSalariedTravelAllowance ?? 0) > 0 && (
                      <Row
                        label={t("payrollTravel.nonSalaried")}
                        value={formatCop(devengados.nonSalariedTravelAllowance)}
                      />
                    )}
                    {devengados.bonificacionesNoSalariales > 0 ? (
                      <Row
                        label={t("liquidations.form.bonif_no_salarial")}
                        value={formatCop(devengados.bonificacionesNoSalariales)}
                      />
                    ) : null}
                    {devengados.incapacidades > 0 ? (
                      <Row
                        label={t("liquidations.detail.incapacidades")}
                        value={formatCop(devengados.incapacidades)}
                      />
                    ) : null}
                    <div className="border-t pt-2">
                      <Row
                        label={t("liquidations.detail.total_devengados")}
                        value={formatCop(liquidation.totalEarnings)}
                        bold
                      />
                    </div>
                  </>
                ) : (
                  <Row
                    label={t("liquidations.detail.total_devengados")}
                    value={formatCop(liquidation.totalEarnings)}
                    bold
                  />
                )}
              </CardContent>
            </Card>

            {/* Deducciones */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("liquidations.detail.deducciones")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                {deducciones ? (
                  <>
                    <Row
                      label={t("liquidations.detail.salud")}
                      value={formatCop(deducciones.saludEmpleado)}
                    />
                    <Row
                      label={t("liquidations.detail.pension")}
                      value={formatCop(deducciones.pensionEmpleado)}
                    />
                    {deducciones.fsp > 0 ? (
                      <Row label="FSP" value={formatCop(deducciones.fsp)} />
                    ) : null}
                    {ibcDetalle?.fsp &&
                    (ibcDetalle.fsp.provisional ||
                      ibcDetalle.fsp.deducidoOtras > 0) ? (
                      <p
                        className="text-[11px] text-muted-foreground"
                        data-testid="liq-fsp-month"
                      >
                        {ibcDetalle.fsp.provisional
                          ? t("liquidations.detail.fsp_provisional")
                          : t("liquidations.detail.fsp_settled", {
                              withheld: formatCop(ibcDetalle.fsp.deducidoOtras),
                            })}
                      </p>
                    ) : null}
                    {deducciones.retefuente > 0 ? (
                      <Row
                        label={t("liquidations.detail.retefuente")}
                        value={formatCop(deducciones.retefuente)}
                      />
                    ) : null}
                    {deducciones.otras > 0 ? (
                      <Row
                        label={t("liquidations.detail.otras")}
                        value={formatCop(deducciones.otras)}
                      />
                    ) : null}
                    <div className="border-t pt-2">
                      <Row
                        label={t("liquidations.detail.total_deducciones")}
                        value={formatCop(liquidation.totalDeductions)}
                        bold
                      />
                    </div>
                  </>
                ) : (
                  <Row
                    label={t("liquidations.detail.total_deducciones")}
                    value={formatCop(liquidation.totalDeductions)}
                    bold
                  />
                )}
              </CardContent>
            </Card>

            {/* Aportes patronales */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">
                    {t("liquidations.detail.aportes")}
                  </CardTitle>
                  {aportes?.exoneracionAplicada ? (
                    <Badge variant="success" className="text-[10px]">
                      {t("liquidations.detail.exonerated_badge")}
                    </Badge>
                  ) : null}
                </div>
                <CardDescription>
                  {t("liquidations.detail.aportes_hint")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <Row
                  label={t("liquidations.detail.salud")}
                  value={formatCop(liquidation.employerHealth)}
                />
                <Row
                  label={t("liquidations.detail.pension")}
                  value={formatCop(liquidation.employerPension)}
                />
                <Row label="ARL" value={formatCop(liquidation.employerArl)} />
                <Row
                  label={t("liquidations.detail.caja")}
                  value={formatCop(liquidation.employerCaja)}
                />
                <Row label="SENA" value={formatCop(liquidation.employerSena)} />
                <Row label="ICBF" value={formatCop(liquidation.employerIcbf)} />
                {ibcDetalle?.incapacidad ? (
                  <p
                    className="pt-1 text-[11px] text-muted-foreground"
                    data-testid="liq-incapacity-bases"
                  >
                    {t("liquidations.detail.incapacity_bases", {
                      worked: formatCop(ibcDetalle.laborado ?? 0),
                      incapacity: formatCop(ibcDetalle.incapacidad),
                    })}
                  </p>
                ) : null}
                {aportes?.licencia && ibcDetalle?.licencia ? (
                  <p
                    className="pt-1 text-[11px] text-muted-foreground"
                    data-testid="liq-leave-contributions"
                  >
                    {t("liquidations.detail.leave_contributions", {
                      health: formatCop(aportes.licencia.salud),
                      pension: formatCop(aportes.licencia.pension),
                      days: ibcDetalle.licencia.days,
                      base: formatCop(ibcDetalle.licencia.baseMensual),
                      source: t(
                        `liquidations.detail.leave_base_${ibcDetalle.licencia.baseFuente}`,
                      ),
                    })}
                  </p>
                ) : null}
                {aportes?.licencia?.financiacion ? (
                  <p
                    className="pt-1 text-[11px] text-muted-foreground"
                    data-testid="liq-high-risk-funded"
                  >
                    {t("liquidations.detail.high_risk_funded", {
                      worker: formatCop(
                        aportes.licencia.pensionTrabajador ?? 0,
                      ),
                      fsp: formatCop(aportes.licencia.fsp ?? 0),
                      revision: aportes.licencia.financiacion.revision,
                    })}
                  </p>
                ) : null}
                {result.exoneracionDetalle ? (
                  <p
                    className="pt-1 text-[11px] text-muted-foreground"
                    data-testid="liq-exemption-month"
                  >
                    {t(
                      result.exoneracionDetalle.elegible
                        ? aportes?.exoneracionAplicada
                          ? "liquidations.detail.exemption_applied"
                          : "liquidations.detail.exemption_not_applied"
                        : "liquidations.detail.exemption_ineligible",
                      {
                        salary: formatCop(
                          result.exoneracionDetalle.devengadoMes,
                        ),
                        threshold: formatCop(result.exoneracionDetalle.umbral),
                      },
                    )}
                    {result.exoneracionDetalle.provisional
                      ? ` ${t("liquidations.detail.exemption_provisional")}`
                      : ""}
                  </p>
                ) : null}
                {aportes?.ajusteExoneracionMes ? (
                  <p
                    className="pt-1 text-[11px] text-muted-foreground"
                    data-testid="liq-exemption-adjustment"
                  >
                    {t("liquidations.detail.exemption_adjustment", {
                      amount: formatCop(
                        aportes.ajusteExoneracionMes.salud +
                          aportes.ajusteExoneracionMes.sena +
                          aportes.ajusteExoneracionMes.icbf,
                      ),
                    })}
                  </p>
                ) : null}
                <div className="border-t pt-2">
                  <Row
                    label={t("liquidations.detail.total_aportes")}
                    value={formatCop(aportes?.total ?? 0)}
                    bold
                  />
                </div>
              </CardContent>
            </Card>

            {/* Provisiones */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("liquidations.detail.provisiones")}
                </CardTitle>
                <CardDescription>
                  {t("liquidations.detail.provisiones_hint")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <Row
                  label={t("liquidations.detail.cesantias")}
                  value={formatCop(liquidation.provisionCesantias)}
                />
                <Row
                  label={t("liquidations.detail.intereses")}
                  value={formatCop(liquidation.provisionIntereses)}
                />
                <Row
                  label={t("liquidations.detail.prima")}
                  value={formatCop(liquidation.provisionPrima)}
                />
                <Row
                  label={t("liquidations.detail.vacaciones")}
                  value={formatCop(liquidation.provisionVacaciones)}
                />
                <div className="border-t pt-2">
                  <Row
                    label={t("liquidations.detail.total_provisiones")}
                    value={formatCop(provisiones?.total ?? 0)}
                    bold
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Flags + links */}
          {(result.flags?.length ?? 0) > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("liquidations.detail.flags")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-inside list-disc space-y-1 text-xs text-muted-foreground">
                  {result.flags!.map((flag, idx) => (
                    <li key={idx}>{flag}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ) : null}

          {liquidation.payslipId || liquidation.journalEntryId ? (
            <Card>
              <CardContent className="flex flex-wrap gap-4 pt-6 text-sm">
                {liquidation.payslipId ? (
                  <Link
                    to="/companies/$companyId/nomina/payslips/$payslipId"
                    params={{ companyId, payslipId: liquidation.payslipId }}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <FileText className="h-4 w-4" />
                    {t("liquidations.detail.payslip_link")}
                  </Link>
                ) : null}
                {liquidation.journalEntryId ? (
                  <Link
                    to="/companies/$companyId/contabilidad/diario/$entryId"
                    params={{ companyId, entryId: liquidation.journalEntryId }}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <FileText className="h-4 w-4" />
                    {t("liquidations.detail.journal_link")}
                  </Link>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          {liquidation.notes ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("liquidations.detail.notes")}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm">{liquidation.notes}</CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("journal.document.title")}
              </CardTitle>
              <CardDescription>
                {t("journal.document.subtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DocumentJournalPanel
                companyId={companyId}
                sourceTable="payroll_liquidations"
                documentId={liquidationId}
              />
            </CardContent>
          </Card>
        </>
      )}

      <DiscardDraftDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t("liquidations.discard.title")}
        description={t("liquidations.discard.body")}
        pending={discardMutation.isPending}
        onConfirm={() => void handleDiscard()}
      />

      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("liquidations.void.title")}</DialogTitle>
            <DialogDescription>
              {liquidation.status === "confirmed"
                ? t("liquidations.void.body_confirmed")
                : t("liquidations.void.body_draft")}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="liq-void-reason">{t("treasury.void.reason")}</Label>
            <textarea
              id="liq-void-reason"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              rows={2}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder={t("treasury.void.reason_placeholder")}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleVoid()}
              disabled={voidMutation.isPending || !voidReason.trim()}
            >
              {t("liquidations.void.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between ${bold ? "font-semibold" : ""}`}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
